import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_URL;

async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuario no autenticado');
  }
  return await user.getIdToken();
}

/**
 * Fetch the authenticated user's org branding (works for any role, including
 * client users who can't read the org doc directly). Returns the branding
 * object or null.
 * @returns {Promise<object|null>}
 */
export async function fetchMyBranding() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE}/api/my-branding`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  // Throw on failure so callers can tell a transient error (keep any cached
  // brand) apart from an authoritative "this org has no branding" (null).
  if (!response.ok) {
    throw new Error(`my-branding request failed (${response.status})`);
  }
  const data = await response.json().catch(() => ({}));
  return data.branding || null;
}

/**
 * Update the current owner's org branding (logo, colors, display name).
 * Owners can only edit their own org; subdomain is not settable here.
 * Empty-string colors/nombreComercial clear that field; `removeLogo: true`
 * drops the logo; `reset: true` reverts the whole org to the Surelia look.
 * @param {{ colorPrimario?: string, colorSecundario?: string, nombreComercial?: string, logo?: File|null, removeLogo?: boolean, reset?: boolean }} input
 * @returns {Promise<{status: string, orgId: string, branding: object}>}
 */
export async function updateOwnBranding(input) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Usuario no autenticado');
  const token = await user.getIdToken();

  const fd = new FormData();
  if (input.colorPrimario !== undefined) fd.append('colorPrimario', input.colorPrimario);
  if (input.colorSecundario !== undefined) fd.append('colorSecundario', input.colorSecundario);
  if (input.nombreComercial !== undefined) fd.append('nombreComercial', input.nombreComercial);
  if (input.removeLogo) fd.append('removeLogo', 'true');
  if (input.reset) fd.append('reset', 'true');
  if (input.logo) fd.append('logo', input.logo, input.logo.name);

  const response = await fetch(`${API_BASE}/api/orgs/branding`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'No se pudo guardar la personalización.');
  }
  return data;
}

/**
 * Request a dedicated subdomain (<orgId>.surelia.mx) for the current owner's
 * org. Flags the org and emails the Surelia team, who provision it manually.
 * @returns {Promise<{status: string}>}
 */
export async function requestSubdomain() {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE}/api/subdomain-request`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 409 && data.code === 'SUBDOMAIN_EXISTS') {
      throw new Error('Esta organización ya tiene un subdominio.');
    }
    if (response.status === 409 && data.code === 'REQUEST_PENDING') {
      throw new Error('Ya existe una solicitud de subdominio pendiente.');
    }
    throw new Error(data.error || 'No se pudo enviar la solicitud.');
  }

  return data;
}
